import * as Sentry from '@sentry/react'

/**
 * Wiring only. The Sentry organisation and DSN are an open decision
 * (plans/00-project-setup.md §12) — without `VITE_SENTRY_DSN` this is a no-op,
 * which is the normal state in development and in CI.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1,

    // An ERP carries customer and financial data. Default to sending none of
    // it and add specific fields deliberately, rather than scrubbing after the
    // fact — a redaction rule that misses is a data-protection incident.
    sendDefaultPii: false,

    beforeSend(event) {
      if (event.request?.cookies) {
        event.request.cookies = undefined
      }

      return event
    },
  })
}

/**
 * The crash boundary's recorder. `reference` is the support reference the
 * user is shown, so it goes in as a TAG - searchable in Sentry, which a
 * context field is not. That is the whole point of the reference.
 *
 * With no DSN this whole module is inert and captureException goes nowhere,
 * so the reference and message are also printed unconditionally: a developer
 * at the keyboard can match what the user quotes. This is not a
 * log-and-continue catch - the boundary still renders the error state.
 */
export function reportBoundaryError(
  error: unknown,
  { reference, componentStack }: { reference?: string; componentStack?: string },
  tags: Record<string, string> = {},
): void {
  console.error('[support-reference]', reference ?? 'none', error)

  Sentry.withScope((scope) => {
    if (reference) scope.setTag('support_reference', reference)
    for (const [key, value] of Object.entries(tags)) scope.setTag(key, value)
    if (componentStack) scope.setContext('react', { componentStack })
    Sentry.captureException(error)
  })
}
